import React from 'react';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import DogTrainingLandingSearchForm from '@rover/react-lib/src/pages/landing/components/DogTrainingLandingSearchForm';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import ServiceLandingServicesWrapper from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServicesWrapper';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import Footer from '@rover/react-lib/src/pages/shared/FooterLinks/Footer';
import { ServiceLandingEnum } from '@rover/types/src/ServiceLanding';

import AppStoreCallout from '../../../../shared/AppStoreCallout/AppStoreCallout';
import TrainingFaqs from '../../../../training/components/FrequentlyAskedQuestions/TrainingFaqs';
import LandingHelmet from '../../../components/LandingHelmet';
import servicesLinksImage from '../../components/images/dog-looking-upward.png';
import ServiceLandingSection from '../../components/ServiceLandingSection';
import ServiceValueProposition from '../../components/ServiceValueProposition';
import puppyTrainingHeroImageDesktop from '../shared-images/Hero_Puppy_desktop.jpg';
import puppyTrainingHeroImageMobile from '../shared-images/Hero_Puppy_mobile.jpg';
import puppyTrainingHeroImageTablet from '../shared-images/Hero_Puppy_tablet.jpg';

const puppyTrainingHeroImages: HeroImageSrcSet = {
  desktop: {
    src: puppyTrainingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: puppyTrainingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: puppyTrainingHeroImageMobile,
    height: 614,
  },
};

const PuppyTraining: React.FC<Props> = ({
  serviceLandingData,
  serviceLinksWithDisplayName,
  locale,
  citiesAndNeighborhoodsLinks,
  countryFooterLinks,
  countryRegionLinks,
}) => {
  const serviceLandingType = ServiceLandingEnum.PUPPY_TRAINING;

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;
  return (
    <>
      <LandingHelmet
        title={seoTitle}
        metaDescription={seoMetaDescription}
        indexable={serviceLandingData.indexable}
      />
      <LandingHero heroImageSrcSet={puppyTrainingHeroImages}>
        <LandingHero.Content width={['100%', '480px']}>
          <LandingHero.Display mt="0x" mb="8x">
            {h1}
          </LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <DogTrainingLandingSearchForm puppy />
          <LandingHero.TrustPilot locale={locale} />
        </LandingHero.Content>
      </LandingHero>

      <ServiceLandingSection>
        <ServiceValueProposition>
          <ServiceValueProposition.Display size="500">
            Professional puppy trainers in your area
          </ServiceValueProposition.Display>
          <ServiceValueProposition.List>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Text>
                Have a private trainer to teach your puppy potty training, basic cues like sit and
                stay, how to address play biting, and more.
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Text>
                Compare trainer skillsets, behavior experience, specialties and pricing to find the
                right trainer for your puppy.
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Text>
                Rest assured knowing trainers on Rover show credentials, commit to rewards-based
                training, and pass a third-party background check.
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
          </ServiceValueProposition.List>
        </ServiceValueProposition>
      </ServiceLandingSection>

      <HowBrandWorks>
        <HowBrandWorks.Display>How puppy training on Rover works</HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>Search</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Read verified reviews and contact trainers who look like a good match.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>Meet trainers</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Chat about training goals and how they can help you and your puppy.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>Schedule sessions</HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              Schedule training sessions and pay through Rover’s secure platform.
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      <ServiceLandingSection>
        <TrainingFaqs isPuppy isFirstQuestionOpen maxWidth="1024px" />
      </ServiceLandingSection>

      <ServiceLandingServicesWrapper
        serviceLandingType={serviceLandingType}
        serviceLinksWithDisplayName={serviceLinksWithDisplayName}
        imageSrc={servicesLinksImage}
      >
        Rover offers many services to meet your pet care needs, including:
      </ServiceLandingServicesWrapper>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout featureToShow="training" />
      </ServiceLandingSection>

      <Footer
        citiesAndNeighborhoodsLinks={citiesAndNeighborhoodsLinks}
        countryFooterLinks={countryFooterLinks}
        countryRegionLinks={countryRegionLinks}
      />
    </>
  );
};

export default PuppyTraining;
